import React, { useMemo, useContext } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCheckbox,
  MDBTypography,
  MDBBtn,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBBadge
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { DevContext } from '../../../../../App';
import { getGeneralConditions, getRequiredGeneralConditions } from '../validation/lectures.js';
import { getLocalizedText } from '../../../../../utils/localizedText';

export default function GeneralConditionsStep({ onComplete, stepId }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, eventConfig } = useValidation();
  const { isDevMode } = useContext(DevContext);

  // Get general conditions (don't depend on lecture type)
  const generalConditions = useMemo(() => {
    if (!eventConfig?.generalConditions) {
      return [];
    }

    return getGeneralConditions(eventConfig);
  }, [eventConfig]);

  // Get required general conditions
  const requiredGeneralConditions = useMemo(() => {
    if (!eventConfig?.generalConditions) {
      return [];
    }

    return getRequiredGeneralConditions(eventConfig);
  }, [eventConfig]);

  // Get current language for displaying condition text
  const currentLanguage = formData.language || "de";

  const handleGeneralConditionChange = (conditionId, checked) => {
    const currentConditions = formData.generalConditions || [];
    const condition = generalConditions.find((c) => c.id === conditionId);

    if (checked) {
      // Add condition if not exists
      const conditionExists = currentConditions.some((c) => c.id === conditionId);
      if (!conditionExists && condition) {
        const newConditions = [
          ...currentConditions,
          {
            id: conditionId,
            accepted: true,
          },
        ];
        handleFieldChange("generalConditions", newConditions);
      }
    } else {
      // Remove condition or mark as not accepted
      const newConditions = currentConditions.filter((c) => c.id !== conditionId);
      handleFieldChange("generalConditions", newConditions);
    }
  };

  const isGeneralConditionChecked = (conditionId) => {
    return formData.generalConditions?.some((c) => c.id === conditionId && c.accepted) || false;
  };

  const areAllRequiredGeneralConditionsChecked = () => {
    return requiredGeneralConditions.every((condition) => isGeneralConditionChecked(condition.id));
  };

  // Get validation info from schema
  const stepValidation = getStepValidation("generalConditions");
  const requiredFields = getStepRequiredFields("generalConditions");

  const handleContinue = () => {
    if (areAllRequiredGeneralConditionsChecked() && stepValidation.isValid) {
      onComplete?.(stepId);
      goToNextStep();
    }
  };

  // Helper function to get condition text in current language
  const getConditionText = (condition) => {
    return getLocalizedText(condition.content) || condition.id;
  };

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5 mt-5">
        <MDBTypography tag="h3" className="mb-4">
          {t("General Conditions")}
        </MDBTypography>

        <MDBTypography tag="p" className="mb-4 text-muted">
          {t("Please read and accept the following general conditions to proceed with your lecture registration.")}
        </MDBTypography>

        {generalConditions.length === 0 && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBAlert color="info">{t("No general conditions configured for this event.")}</MDBAlert>
            </MDBCol>
          </MDBRow>
        )}

        {generalConditions.length > 0 && (
          <MDBRow className="mb-4">
            <MDBCol>
              {generalConditions.map((condition) => (
                <MDBCard key={condition.id} style={{ "max-width": "900px" }} className={` mb-4 shadow-sm ${isGeneralConditionChecked(condition.id) ? "border border-3 border-success" : ""}`}>
                  <MDBCardBody className="d-flex flex-column justify-content-between gap-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <MDBTypography tag="h4" className="mb-0">
                        {getLocalizedText(condition.title)}
                      </MDBTypography>
                      {condition.required && (
                        <MDBBadge color="info" pill>
                          {t("Required")}
                        </MDBBadge>
                      )}
                    </div>
                    <div>
                      <MDBTypography tag="p" className="mb-3">
                        {getLocalizedText(condition.content)}
                      </MDBTypography>
                    </div>
                    <div className="d-flex justify-content-end">
                      <MDBCheckbox
                        name={condition.id}
                        id={condition.id}
                        label={getLocalizedText(condition.checkboxLabel)}
                        labelClass="fw-bold"
                        checked={isGeneralConditionChecked(condition.id)}
                        onChange={(e) => handleGeneralConditionChange(condition.id, e.target.checked)}
                      />
                    </div>
                  </MDBCardBody>
                </MDBCard>
              ))}
            </MDBCol>
          </MDBRow>
        )}

        {hasFieldError("generalConditions") && (
          <MDBRow className="mb-3">
            <MDBCol>
              <MDBAlert color="danger">{getFieldError("generalConditions")}</MDBAlert>
            </MDBCol>
          </MDBRow>
        )}

        {/* Validation Debug Info */}
        {isDevMode && (
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBCard className="border-info">
                <MDBCardBody>
                  <MDBTypography tag="h6" className="mb-3">
                    🔍 Validation Debug Info (Dev Mode) - General Conditions Step
                  </MDBTypography>

                  <div className="mb-2">
                    <strong>General Conditions:</strong>
                    <ul className="mb-2">
                      <li>Available: {generalConditions.length} general conditions</li>
                      <li>Required: {requiredGeneralConditions.length} conditions must be accepted</li>
                      <li>Current Accepted: {formData.generalConditions?.filter((c) => c.accepted)?.length || 0}</li>
                      <li>Step Required Fields: {requiredFields.join(", ") || "none"}</li>
                    </ul>
                  </div>

                  {generalConditions.length > 0 && (
                    <div className="mb-2">
                      <strong>Condition Details:</strong>
                      <ul className="mb-2">
                        {generalConditions.map((condition) => (
                          <li key={condition.id}>
                            <code>{condition.id}</code>: {condition.required ? "Required" : "Optional"} -{isGeneralConditionChecked(condition.id) ? " ✓ Accepted" : " ✗ Not accepted"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mb-2">
                    <strong>Validation Status:</strong> <MDBBadge color={stepValidation.isValid ? "success" : "danger"}>{stepValidation.isValid ? "Valid" : "Invalid"}</MDBBadge>
                  </div>

                  {stepValidation.errors.length > 0 && (
                    <div className="mb-2">
                      <strong>Schema Errors:</strong>
                      <ul className="mb-0">
                        {stepValidation.errors.map((error, index) => (
                          <li key={index}>
                            <code>{error.field}</code>: {error.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mb-2">
                    <strong>Continue Requirements:</strong>
                    <ul className="mb-2">
                      <li>All required general conditions checked: {areAllRequiredGeneralConditionsChecked() ? "✓" : "✗"}</li>
                      <li>Schema validation passed: {stepValidation.isValid ? "✓" : "✗"}</li>
                    </ul>
                  </div>

                  <div className="mb-2">
                    <strong>Continue Button:</strong>{" "}
                    <MDBBadge color={areAllRequiredGeneralConditionsChecked() && stepValidation.isValid ? "success" : "warning"}>
                      {areAllRequiredGeneralConditionsChecked() && stepValidation.isValid ? "Enabled" : "Disabled"}
                    </MDBBadge>
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>
        )}
      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center">
              <MDBBtn onClick={handleContinue} disabled={!areAllRequiredGeneralConditionsChecked() || !stepValidation.isValid}>
                {t("Continue")}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}
